import type { CondenseContentConfig } from './Schemas';
import { condenseContentConfigSchema } from './Schemas';

const SECTION_CLASS = 'ss-condensed';
const TITLE_CLASS = 'ss-condensed-title';
const BODY_CLASS = 'ss-condensed-body';
const CHEVRON_CLASS = 'ss-condensed-chevron';
const OPEN_CLASS = 'ss-open';
const READY_ATTRIBUTE = 'data-condense-ready';

// Page Settings stores the admin's choices by settings key, while public markup
// tags each component with a `data-comp` slug. Only accordion differs between the
// two vocabularies, but the map keeps the translation in one place.
const DATA_COMP_BY_SETTINGS_KEY: Record<string, string> = {
    'accordion-tabs': 'accordion',
    announcements: 'announcements',
    columns: 'columns',
    document: 'document',
    'icon-matrix': 'icon-matrix',
    links: 'links',
};

let instanceCount = 0;

function init(): void {
    const config = getActiveConfig();
    if (!config) {
        return;
    }

    config.components.forEach(condenseComponentsOfType);
}

function getActiveConfig(): CondenseContentConfig | null {
    const config = parseConfig();
    if (!config?.enabled) {
        return null;
    }

    return config;
}

function parseConfig(): CondenseContentConfig | null {
    const dataElement = document.querySelector<HTMLScriptElement>(
        'script[type="application/json"][data-view-data-condense-content]',
    );
    if (!dataElement?.textContent) {
        return null;
    }

    try {
        const result = condenseContentConfigSchema.safeParse(
            JSON.parse(dataElement.textContent),
        );
        if (!result.success) {
            return null;
        }

        dataElement.remove();
        return result.data;
    } catch {
        return null;
    }
}

function condenseComponentsOfType(settingsKey: string): void {
    const dataComp = DATA_COMP_BY_SETTINGS_KEY[settingsKey];
    if (!dataComp) {
        return;
    }

    const sections = document.querySelectorAll<HTMLElement>(
        `section[data-comp="${dataComp}"]`,
    );
    sections.forEach(condenseSection);
}

function condenseSection(section: HTMLElement): void {
    if (section.hasAttribute(READY_ATTRIBUTE)) {
        return;
    }

    const title = section.querySelector<HTMLElement>(
        '.ss-component-header-title',
    );
    const body = section.querySelector<HTMLElement>('.ss-component-content');

    // Components render no heading at all when the admin leaves the optional
    // heading blank, which leaves nothing to turn into a toggle affordance.
    if (!title || !body) {
        return;
    }

    instanceCount += 1;
    const instanceId = section.id || `ss-condense-${instanceCount}`;
    const titleId = `${instanceId}-title`;
    const bodyId = `${instanceId}-body`;

    prepareBody(body, bodyId, titleId);
    const button = buildToggleButton(title, titleId, bodyId);

    section.classList.add(SECTION_CLASS);
    title.classList.add(TITLE_CLASS);
    section.setAttribute(READY_ATTRIBUTE, 'true');

    button.addEventListener('click', handleToggle);
}

function prepareBody(body: HTMLElement, bodyId: string, titleId: string): void {
    body.id = bodyId;
    body.classList.add(BODY_CLASS);
    body.setAttribute('role', 'region');
    body.setAttribute('aria-labelledby', titleId);
}

function buildToggleButton(
    title: HTMLElement,
    titleId: string,
    bodyId: string,
): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.id = titleId;
    button.className = 'ss-button';
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', bodyId);

    while (title.firstChild) {
        button.appendChild(title.firstChild);
    }

    const chevron = document.createElement('span');
    chevron.className = `fa fa-chevron-down ${CHEVRON_CLASS}`;
    chevron.setAttribute('aria-hidden', 'true');
    button.appendChild(chevron);

    title.appendChild(button);

    return button;
}

function dispatchWindowResize(): void {
    window.dispatchEvent(new Event('resize'));
}

function refreshLayoutAfterExpand(section: HTMLElement): void {
    requestAnimationFrame(() => {
        const body = section.querySelector<HTMLElement>(`.${BODY_CLASS}`);
        if (!body || body.offsetWidth === 0) {
            requestAnimationFrame(dispatchWindowResize);
            return;
        }

        dispatchWindowResize();
    });
}

function handleToggle(event: Event): void {
    const button = event.currentTarget as HTMLButtonElement;
    const section = button.closest<HTMLElement>(`.${SECTION_CLASS}`);
    if (!section) {
        return;
    }

    const isOpen = section.classList.toggle(OPEN_CLASS);
    button.setAttribute('aria-expanded', String(isOpen));

    if (isOpen) {
        refreshLayoutAfterExpand(section);
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

export {};
